<template>
  <Card class="container flex flex-col justify-between">
    <h1 class="lg:text-2xl font-bold mb-4 truncate">
      Users and Organizations {{ displayName }} is following
    </h1>
    <NamiLoading v-if="initialLoading" />
    <NoResults
      v-else-if="userOrgsFollowing.length === 0"
      :message="translate('site.user.id.following.no_follows')"
    />
    <ul
      v-else
      class="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
    >
      <li v-for="followingEntity in userOrgsFollowing">
        <UserProfileCard
          v-if="followingEntity.type === 'user'"
          :user="followingEntity.user"
          v-model:following="followingEntity.iFollowThem"
          :follows-you="followingEntity.theyFollowMe"
        />
        <OrganizationProfileCard
          v-else-if="followingEntity.type === 'organization'"
          class="h-full"
          :org="followingEntity.org"
          v-model:following="followingEntity.iFollowThem"
        />
      </li>
    </ul>
    <NamiLoading v-if="(hasMoreUsers || hasMoreOrgs) && !initialLoading" />
  </Card>
</template>

<script setup lang="ts">
import { User, Organization, isExpanded, isUser, isOrg } from "~/src/api";
import type {
  UserEntity,
  UserFollow,
  UserRelation,
  OrganizationRelation,
  PopulateRelationship,
  OrganizationFollowsEntity,
} from "~/src/api";

type UserFollowing = {
  type: "user";
  user: PopulateRelationship<UserRelation>;
  iFollowThem: boolean;
  theyFollowMe: boolean;
};

type OrganizationFollowing = {
  type: "organization";
  org: PopulateRelationship<OrganizationRelation>;
  iFollowThem: boolean;
};

const props = defineProps<{
  user: UserEntity;
}>();

const nuxtApp = useNuxtApp();
const auth = nuxtApp.$auth();
const { t: translate } = nuxtApp.$i18n.global;

const {
  limit: userLimit,
  page: userPage,
  offset: userOffset,
} = useInternalPagination(1, 16);

const {
  limit: orgLimit,
  page: orgPage,
  offset: orgOffset,
} = useInternalPagination(1, 16);

const hasMoreUsers = ref(true);
const hasMoreOrgs = ref(true);
const initialLoading = ref(true);

const userOrgsFollowing = reactive<(UserFollowing | OrganizationFollowing)[]>(
  [],
);

const displayName = computed(
  () => props.user.attributes.displayName ?? props.user.attributes.username,
);

const { pending: fetchNextFollowersPending, action: fetchNextFollowers } =
  useAction2(async () => {
    const usersFollowing = await getNextFollowingUsers();
    const orgsFollowing = await getNextFollowingOrgs();
    userOrgsFollowing.push(...shuffleUserOrgs(usersFollowing, orgsFollowing));
  });

async function getNextFollowingUsers(): Promise<UserFollowing[]> {
  if (!hasMoreUsers.value) return [];

  const usersThatAreBeingFollowedByThisPerson = await User.getFollows({
    originId: props.user.id,
    includes: ["user"],
    offset: userOffset.value,
    limit: userLimit.value,
  });

  hasMoreUsers.value =
    usersThatAreBeingFollowedByThisPerson.meta.total >
    userLimit.value + userOffset.value;
  userPage.value++;

  const ofWhichIds = usersThatAreBeingFollowedByThisPerson.data.map(
    (userFollowEntity) => userFollowEntity.id,
  );

  const ofWhichThatFollowMe =
    await filterUserIdsByThoseThatFollowMe(ofWhichIds);
  const thoseOfWhichIFollow = await filterUserIdsByThoseThatIFollow(ofWhichIds);

  const userIds = usersThatAreBeingFollowedByThisPerson.data
    .map((user) => getUserFromUserFollow(user)?.id)
    .filter(onlyTruthys);

  const res = await getUsersFromIds(
    userIds,
    ["profile_frame", "badge"],
    await auth?.getToken(),
  );

  return usersThatAreBeingFollowedByThisPerson.data
    .map((user) => {
      const relatedUser = res.data.find((u) => u.id === user.id);
      if (!relatedUser) return null;

      return {
        type: "user",
        user: relatedUser,
        theyFollowMe: ofWhichThatFollowMe.includes(relatedUser.id),
        iFollowThem: thoseOfWhichIFollow.includes(relatedUser.id),
      } satisfies UserFollowing;
    })
    .filter(<T,>(x: T | null): x is T => !!x);
}

async function getNextFollowingOrgs(): Promise<OrganizationFollowing[]> {
  if (!hasMoreOrgs.value) return [];

  const orgsFollowing = await Organization.getFollows({
    userId: props.user.id,
    includes: ["organization"],
    limit: orgLimit.value,
    offset: orgOffset.value,
  });

  hasMoreOrgs.value =
    orgsFollowing.meta.total > orgLimit.value + orgOffset.value;
  orgPage.value++;

  const ofWhichIds = orgsFollowing.data.map(
    (orgFollowEntity) => orgFollowEntity.id,
  );

  const thoseOfWhichThatIFollow =
    await filterOrgIdsByThoseThatIFollow(ofWhichIds);

  return orgsFollowing.data
    .map((org) => {
      const relatedOrg = getOrgFromOrgFollow(org);
      if (!relatedOrg || !isExpanded(relatedOrg)) return null;

      return {
        type: "organization",
        org: relatedOrg,
        iFollowThem: thoseOfWhichThatIFollow.includes(relatedOrg.id),
      } satisfies OrganizationFollowing;
    })
    .filter(<T,>(x: T | null): x is T => !!x);
}

function getUserFromUserFollow(userFollowEntity: UserFollow) {
  return userFollowEntity.relationships
    .filter(isUser)
    .find((user) => user.id === userFollowEntity.id);
}

function getOrgFromOrgFollow(org: OrganizationFollowsEntity) {
  return org.relationships.find(isOrg);
}

function shuffleUserOrgs(
  users: UserFollowing[],
  orgs: OrganizationFollowing[],
) {
  const shuffledArray = [...users, ...orgs];
  for (let i = shuffledArray.length - 1; i > 0; --i) {
    const randomIndex = Math.floor(Math.random() * i);
    [shuffledArray[i], shuffledArray[randomIndex]] = [
      shuffledArray[randomIndex],
      shuffledArray[i],
    ];
  }
  return shuffledArray;
}

function loadMoreIfNearBottom() {
  if (
    fetchNextFollowersPending.value ||
    (!hasMoreUsers.value && !hasMoreOrgs.value)
  )
    return;

  if (
    window.scrollY + window.innerHeight >
    document.documentElement.scrollHeight - 300
  )
    fetchNextFollowers();
}

async function filterUserIdsByThoseThatFollowMe(
  userIds: string[],
): Promise<string[]> {
  const myUserId = auth?.userEntity?.id;
  if (!myUserId) return [];

  const usersThatFollowMe = await User.getFollows({
    limit: userIds.length,
    originIds: userIds,
    targetId: myUserId,
  });

  const ofWhichIds = usersThatFollowMe.data
    .map((userFollowEntity) => {
      const userRelationshipThatIsntMe = userFollowEntity.relationships.find(
        (rel) => rel.id !== myUserId,
      );
      return userRelationshipThatIsntMe?.id;
    })
    .filter(<T,>(id: T | undefined): id is T => !!id);

  const thoseOfWhichThatFollowMe = userIds.filter((id) =>
    ofWhichIds.includes(id),
  );
  return thoseOfWhichThatFollowMe;
}

async function filterUserIdsByThoseThatIFollow(userIds: string[]) {
  const myUserId = auth?.userEntity?.id;
  if (!myUserId) return [];

  const usersThatIFollow = await User.getFollows({
    limit: userIds.length,
    originId: myUserId,
    targetIds: userIds,
  });

  const ofWhichIds = usersThatIFollow.data
    .map((userFollowEntity) => {
      const userRelationshipThatIsntMe = userFollowEntity.relationships.find(
        (rel) => rel.id !== myUserId,
      );
      return userRelationshipThatIsntMe?.id;
    })
    .filter(<T,>(id: T | undefined): id is T => !!id);

  const thoseOfWhichThatIFollow = userIds.filter((id) =>
    ofWhichIds.includes(id),
  );
  return thoseOfWhichThatIFollow;
}

async function filterOrgIdsByThoseThatIFollow(orgIds: string[]) {
  const myUserId = auth?.userEntity?.id;
  if (!myUserId) return [];

  const orgsThatIFollow = await Organization.getFollows({
    limit: orgIds.length,
    userId: myUserId,
    organizationIds: orgIds,
  });

  const ofWhichIds = orgsThatIFollow.data
    .map((orgFollowEntity) => {
      const userRelationshipThatIsntMe = orgFollowEntity.relationships.find(
        (rel) => rel.type === "organization",
      );
      return userRelationshipThatIsntMe?.id;
    })
    .filter(<T,>(id: T | undefined): id is T => !!id);

  const thoseOfWhichThatIFollow = orgIds.filter((id) =>
    ofWhichIds.includes(id),
  );
  return thoseOfWhichThatIFollow;
}

onMounted(() => {
  fetchNextFollowers().then(() => (initialLoading.value = false));
  window.addEventListener("scroll", loadMoreIfNearBottom);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", loadMoreIfNearBottom);
});
</script>
